import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import premioElbb from 'assets/img/2026/premio_elbb.png';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativas } from 'services/iniciativa.service';
import { Header } from '../HomePublic/components/Header';
import { HeroIniciativas } from './components/HeroIniciativas';
import { FiltrosIniciativas } from './components/FiltrosIniciativas';
import { IniciativaCard } from '../HomePublic/components/IniciativaCard';
import './style.scss';

export function Iniciativas() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [categoriaSelecionada, setCategoriaSelecionada] = useState('todos');
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [iniciativas, setIniciativas] = useState<any[]>([]);
  const [iniciativasFiltradas, setIniciativasFiltradas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    async function carregarIniciativas() {
      try {
        setLoading(true);
        const data = await buscarIniciativas({
          start: "2026-01-01T00:00:00-03:00",
          userId: user?._id
        });
        // Filtrar ELBB - não deve aparecer na página Iniciativas
        const iniciativasFiltradas = data.filter(
          (ini: any) => ini.categoryPremio !== 'Prêmio ELBB'
        );
        setIniciativas(iniciativasFiltradas || []);
      } catch (error) {
        setIniciativas([]);
      } finally {
        setLoading(false);
      }
    }

    if (user?._id) {
      carregarIniciativas();
    }
  }, [user]);

  // Aplicar filtros quando mudar categoria, busca ou iniciativas
  useEffect(() => {
    let resultado = [...iniciativas];

    // Filtrar por categoria
    if (categoriaFiltro) {
      resultado = resultado.filter((ini) => {
        // Mapear categoryPremio para os valores do filtro
        const categoriaMap: Record<string, string[]> = {
          'JOGA_JUNTO': ['Joga Junto'],
          'CONEXAO': ['Prêmio Conexão'],
        };
        
        const categoriasAceitas = categoriaMap[categoriaFiltro] || [];
        return categoriasAceitas.includes(ini.categoryPremio);
      });
    }

    // Sincronizar categoria selecionada do Hero com filtro
    if (categoriaSelecionada && categoriaSelecionada !== 'todos') {
      resultado = resultado.filter((ini) => {
        const categoriaMap: Record<string, string[]> = {
          'JOGA_JUNTO': ['Joga Junto'],
          'CONEXAO': ['Prêmio Conexão', 'Prêmio Conceito'],
        };
        
        const categoriasAceitas = categoriaMap[categoriaSelecionada] || [];
        return categoriasAceitas.includes(ini.categoryPremio);
      });
    }

    // Filtrar por termo de busca (título, descrição, autores, tema)
    if (termoBusca.trim()) {
      const termoLower = termoBusca.toLowerCase();
      resultado = resultado.filter((ini) => {
        return (
          ini.titulo?.toLowerCase().includes(termoLower) ||
          ini.descricao?.toLowerCase().includes(termoLower) ||
          ini.autores?.toLowerCase().includes(termoLower) ||
          ini.tema?.toLowerCase().includes(termoLower) ||
          ini.diretoria?.toLowerCase().includes(termoLower)
        );
      });
    }

    setIniciativasFiltradas(resultado);
    setPaginaAtual(1); // Resetar para primeira página ao filtrar
  }, [iniciativas, categoriaFiltro, termoBusca, categoriaSelecionada]);

  const iniciativasPorPagina = 9;
  const totalPaginas = Math.ceil(iniciativasFiltradas.length / iniciativasPorPagina);
  const indexInicio = (paginaAtual - 1) * iniciativasPorPagina;
  const indexFim = indexInicio + iniciativasPorPagina;
  const iniciativasVisiveis = iniciativasFiltradas.slice(indexInicio, indexFim);

  const handleBuscar = () => {
    // A busca já é aplicada via useEffect
  };

  const handlePaginaChange = (pagina: number) => {
    setPaginaAtual(pagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="iniciativas-wrapper">
      <Header currentPage="iniciativas" />

      <HeroIniciativas
        categoriaSelecionada={categoriaSelecionada}
        onCategoriaChange={setCategoriaSelecionada}
      />

      <FiltrosIniciativas
        categoriaSelecionada={categoriaFiltro}
        onCategoriaChange={setCategoriaFiltro}
        termoBusca={termoBusca}
        onBuscaChange={setTermoBusca}
        onBuscar={handleBuscar}
      />

      <main className="iniciativas-main">
        <div className="iniciativas-main__container">
          <div className="iniciativas-main__header">
            <h2 className="iniciativas-main__count">
              {iniciativasFiltradas.length} iniciativas encontradas
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <span className="text-blue-bb">Carregando iniciativas...</span>
            </div>
          ) : (
            <>
              <div className="iniciativas-main__grid">
                {iniciativasVisiveis.map((iniciativa) => (
                  <IniciativaCard
                    key={iniciativa.id}
                    {...iniciativa}
                  />
                ))}
                {/* Card do Prêmio ELBB sempre como último card */}
                <div className="premio-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/login-elbb')}>
                  <div className="premio-card__inner">
                    <p className="premio-card__subtitle">Conheça também as</p>
                    <p className="premio-card__subtitle">iniciativas do</p>
                    <div className="premio-card__logo">
                      <img src={premioElbb} alt="Prêmio ELBB" />
                    </div>
                    <button className="premio-card__button">QUERO CONHECER!</button>
                  </div>
                </div>
              </div>

              {totalPaginas > 1 && (
                <div className="iniciativas-main__pagination">
                  <button
                    className="pagination__btn pagination__btn--prev"
                    onClick={() => handlePaginaChange(paginaAtual - 1)}
                    disabled={paginaAtual === 1}
                    aria-label="Página anterior"
                  >
                    ‹
                  </button>

                  <div className="pagination__numbers">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
                      <button
                        key={pagina}
                        className={`pagination__number ${paginaAtual === pagina ? 'active' : ''}`}
                        onClick={() => handlePaginaChange(pagina)}
                      >
                        {pagina}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination__btn pagination__btn--next"
                    onClick={() => handlePaginaChange(paginaAtual + 1)}
                    disabled={paginaAtual === totalPaginas}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
